import { NetworkAppContainerWebComponent } from '../component/NetworkAppContainerWebComponent';
import { NetworkAppContainerRepository } from '../repository/NetworkAppContainerRepository';
import {
  BaseWebComponentController,
  IListenerConfig,
  WebComponentEvent,
} from '../../../framework/BaseWebComponentController';
import { NetworkAppContainerView } from '../view/NetworkAppContainerView';
import { templateNetworkAppContainer } from '../view/template';
import {
  Events,
  GuidedTourSetterResponse,
  InitNetworkData,
  NETWORK_GUIDED_TOUR_HTML_ELEMENTS_NEED_PREPARING,
  NetworkAction,
  NetworkActionsToPopupsMap,
  NetworkGridItem,
  NetworkV2Analytics,
  SmartViewIdToAnalyticsNameMap,
  ValidationHistoryMap,
  NETWORK_DATA_PROVIDER_ERROR,
} from '../helpers/types';
import { MegaMenuEvents, MegaMenuItem, MegaMenuSection } from '../../../ui/gzMegaMenu/types';
import { NetworkPopUpFactory, NetworkPopUpType } from '../../networkPopUps/network.pop-up.factory';
import { NetworkActionValidatorsFactory } from '../factory/NetworkActionValidatorsFactory';
import { FilterEvents, GridEvents } from '../../../grid/types/common';
import { BaseNetworkActionValidator } from '../factory/action-validators/base.network.action.validator';
import { ProtectedEntity } from '@nirvana/shared/dtos/types/protected-entities/common';
import { MegaMenuHelper } from '../helpers/megaMenu/mega.menu.helper';
import { ToggleEvents } from '../../../ui/gzToggle/types';
import { NetworkTreeEvent, TreeDetails } from '../../networkTree/types';
import { NavigationHelper } from '@nirvana/shared/navigation';
import { TreeSearchEvent } from '../../networkTreeSearch/types';
import { elementClassListAdd, elementClassListRemove, toggleVisibility } from '../../../utils/htmlElement.utils';
import { SmartViewEvents, SmartViewState } from '../../../grid/genericGridSmartViews/types';
import { GenericGridSmartViewsHelpers } from '../../../grid/genericGridSmartViews/helpers/GenericGridSmartViewsHelpers';
import { cloneDeep, isEqual } from 'lodash';
import { DATA_LOAD_MORE } from '../../../grid/utils/constants';
import { ButtonEvents } from '../../../ui/gzButton/types';
import { AFTER_UNMARK_EVENT_NAME } from '../../networkPopUps/popUps/goldenImage/unMark/UnmarkGoldenImagePopUp';
import { AFTER_MARK_EVENT_NAME } from '../../networkPopUps/popUps/goldenImage/mark/MarkGoldenImagePopUp';
import { DeleteActionHelper } from '../helpers/actions/specifics/delete.action.helper';
import { getTreeDetailsForCompanyId } from '../../networkTree/helpers/tree.helper';
import { MegaMenuAction, RightClickExtraActions } from '../helpers/megaMenu/types';
import { SmartViewsHelper } from '../helpers/smartViews/smart.views.helper';
import { NetworkViewGridConfig } from '../helpers/smartViews/types';
import { AD_CHANGE_EVENT, NewADFolderEvent } from '../../networkPopUps/popUps/setADIntegrator/component/interfaces';
import { PopUpRenderData } from '../../networkPopUps/types';
import { NetworkActionsHelperFactory } from '../helpers/actions/network.actions.helper.factory';
import { NODE_ADDED } from '../../../ui/gzTree/uiElements/gzNodeRenameInput/helpers/types';
import { MoveFinishedEventDetail, MoveToEvents } from '../../networkPopUps/popUps/moveTo/types';
import { ProtectedEntitiesHelper } from '../helpers/protectedEntities/protected.entities.helper';
import { EC2Events } from '../../networkPopUps/popUps/syncWithEC2/types';
import { AFTER_ASSIGN_VMWARE_EVENT_NAME } from '../../networkPopUps/popUps/setVmwareIntegrator/SetVmwareIntegratorPopUp';
import { AFTER_UNASSIGN_VMWARE_EVENT_NAME } from '../../networkPopUps/popUps/unsetVmwareIntegrator/UnsetVmwareIntegratorPopUp';
import { GridFilterRenderEvents } from '../../../grid/gridFilters/types';
import { GuidedToursManager } from '../../../guidedTours/GuidedToursManager';
import { GuidedTourSections } from '../../../guidedTours/types';
import { GridSettingsEvents } from 'apps/webcomponents/src/grid/gridSettings/types';
import { RowRightClickEvent } from '../../../grid/gridView/types';
import { ContextMenuOverlayEvents } from '../../../ui/gzContextMenuOverlay/component/gzContextMenuOverlay';
import { overlayOpenListener } from '../../../riskManagement/common/utils';
import { MultiLevelListEvents, MultiLevelListItem } from '../../../ui/gzMultiLevelList/types';
import { TargetType } from '../../networkScrollableWrapper/types';
import { NavButtonEvent, NavButtonType } from '../gzNavigationButton/types';
import { GridViewStatusChipEvent } from 'apps/webcomponents/src/grid/uiElements/gridViewStatusChip/types';

export class NetworkAppContainerController extends BaseWebComponentController {
  public component: NetworkAppContainerWebComponent;
  public dataRepository: NetworkAppContainerRepository;
  public view: NetworkAppContainerView;
  public viewConstructor: typeof NetworkAppContainerView;
  public dataRepositoryConstructor: typeof NetworkAppContainerRepository;

  //properties
  private megaMenuSections: Array<MegaMenuSection>;
  private megaMenuActions: Array<MegaMenuAction> = [];
  private selectedItemsData: Array<NetworkGridItem> = [];
  private readonly userData: UserData;
  private guidedTourElementsLoaded = 0;
  /**
   * Fast disable of feature. Convert to true if you want to disable the after click
   * behavior of an entity that is not endpoint in the network section.
   */
  private readonly nameLinkDisabled = false;

  constructor(component: NetworkAppContainerWebComponent) {
    super(component);
    this.initialSetup({
      viewConstructor: NetworkAppContainerView,
      viewTemplate: templateNetworkAppContainer,
      dataRepositoryConstructor: NetworkAppContainerRepository,
    });

    if (!this.userData) {
      this.userData = Application.Main.getUser();
    }
  }

  public computeEventListeners(): IListenerConfig[] {
    return [
      {
        element: this.view.dom.$grid,
        event: GridEvents.SELECT_ROWS,
        callback: this.handleGridSelectRows.bind(this),
      },
      {
        element: this.view.dom.$globalActionsButton,
        event: ButtonEvents.CLICK,
        callback: this.handleGlobalActionsButtonClick.bind(this),
      },
      {
        element: this.view.dom.$reportsButton,
        event: ButtonEvents.CLICK,
        callback: this.handleReportsButtonClick.bind(this),
      },
      {
        element: this.view.dom.$recoveryManagerButton,
        event: ButtonEvents.CLICK,
        callback: this.handleRecoveryManagerButtonClick.bind(this),
      },
      {
        element: this.view.dom.$megaMenu,
        event: MegaMenuEvents.ITEM_CLICKED,
        callback: this.handleMegaMenuItemClick.bind(this),
      },
      {
        element: this.view.dom.$megaMenu,
        event: MegaMenuEvents.COMPUTE_MEGA_MENU_SECTIONS,
        callback: this.computeMegaMenuSections.bind(this),
      },
      {
        element: this.view.dom.$depthToggle,
        event: ToggleEvents.TOGGLE_CHANGE,
        callback: this.onChangeDepth,
      },
      {
        element: this.view.dom.$treeWrapper,
        event: NetworkTreeEvent.NODE_CHANGED,
        callback: this.handleTreeChange,
      },
      {
        element: this.view.dom.$treeWrapper,
        event: TreeSearchEvent.START_FETCHING,
        callback: this.handleTreeStartFetching,
      },
      {
        element: this.view.dom.$treeWrapper,
        event: TreeSearchEvent.INPUT_RESET,
        callback: this.handleTreeInputReset,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.GRID_SELECT_ALL,
        callback: this.handleGridSelectAll,
      },
      {
        element: this.view.dom.$grid,
        event: Events.LABEL_ROW_CLICKED,
        callback: this.handleNameClicked,
      },
      {
        element: this.view.dom.$grid,
        event: Events.USER_COLUMN_CLICKED,
        callback: this.handleUserColumnClicked,
      },
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_SELECTED,
        callback: this.handleSmartViewClicked,
      },
      {
        element: this.view.dom.$grid,
        event: SmartViewEvents.SMART_VIEW_SAVED,
        callback: this.updateViewProps,
      },
      {
        element: this.view.dom.$grid,
        event: SmartViewEvents.SHOW_SAVE_VIEW_AS_DIALOG,
        callback: this.showSaveAsDialog,
      },
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_AFTER_CREATE,
        callback: this.handleSmartViewAfterCreate,
      },
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_NAME_UPDATED,
        callback: this.handleSmartViewNameUpdated,
      },
      {
        element: this.view.dom.$smartViews,
        event: SmartViewEvents.SMART_VIEW_AFTER_DELETE,
        callback: this.handleSmartViewAfterDelete,
      },
      {
        element: this.view.dom.$grid,
        event: SmartViewEvents.SMART_VIEW_FAVORITE_CHANGED,
        callback: this.handleSmartViewFavoriteChanged,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.AFTER_DISCARD_CHANGES,
        callback: this.handleDiscardChanges,
      },
      {
        element: this.view.dom.$goToContainerButton,
        event: ButtonEvents.CLICK,
        callback: this.handleGoToContainer,
      },
      {
        element: this.view.dom.$wrapper,
        event: AFTER_UNMARK_EVENT_NAME,
        callback: this.handleRefreshGrid,
      },
      {
        element: this.view.dom.$wrapper,
        event: AFTER_MARK_EVENT_NAME,
        callback: this.handleRefreshGrid,
      },
      {
        element: this.view.dom.$deleteEntitiesButton,
        event: ButtonEvents.CLICK,
        callback: this.handleDeleteEntitiesButtonClicked,
      },
      {
        element: this.view.dom.$container,
        event: Events.DELETE_ENTITIES_SUCCESS,
        callback: this.handleDeleteEntities,
      },
      {
        element: this.view.dom.$container,
        event: MoveToEvents.MOVE_ENTITIES_FINISHED,
        callback: this.handleMoveFinishedAction,
      },
      {
        element: this.view.dom.$container,
        event: MoveToEvents.MOVE_COMPANIES_FINISHED,
        callback: this.refreshTree,
      },
      {
        element: this.view.dom.$container,
        event: NODE_ADDED,
        callback: this.handleRefreshGrid,
      },
      {
        element: this.view.dom.$wrapper,
        event: AD_CHANGE_EVENT,
        callback: this.handleChangedAD,
      },
      {
        element: this.view.dom.$wrapper,
        event: EC2Events.INIT_SYNC,
        callback: this.handleEC2SyncInitiated,
      },
      {
        element: this.view.dom.$wrapper,
        event: EC2Events.FINISHED_SYNC,
        callback: this.handleEC2SyncFinished,
      },
      {
        element: this.view.dom.$treeWrapper,
        event: EC2Events.INIT_SYNC,
        callback: this.handleEC2SyncInitiated,
      },
      {
        element: this.view.dom.$treeWrapper,
        event: EC2Events.FINISHED_SYNC,
        callback: this.handleEC2SyncFinished,
      },
      {
        element: this.view.dom.$container,
        event: AFTER_ASSIGN_VMWARE_EVENT_NAME,
        callback: this.refreshGridAndTree,
      },
      {
        element: this.view.dom.$container,
        event: AFTER_UNASSIGN_VMWARE_EVENT_NAME,
        callback: this.refreshGridAndTree,
      },
      {
        element: this.view.dom.$grid,
        event: GridSettingsEvents.SETTINGS_UPDATED,
        callback: this.syncStateInNetworkStorage,
      },
      {
        element: this.view.dom.$grid,
        event: FilterEvents.FILTERS_UPDATED,
        callback: this.syncStateInNetworkStorage,
      },
      {
        element: this.view.dom.$tree,
        event: NetworkTreeEvent.TREE_LOADED,
        callback: this.prepareTreeTourElement,
      },
      {
        element: this.view.dom.$grid,
        event: GridFilterRenderEvents.FILTERS_RENDER_DONE,
        callback: this.prepareFiltersTourElement,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROWS_LOADING_FINISHED,
        callback: this.prepareIconsAndActionsTourElements,
      },
      {
        element: this.view.dom.$grid,
        event: GridEvents.ROW_RIGHT_CLICKED,
        callback: this.handleRowRightClicked,
      },
      {
        // This was needed in order to modify the pointer-events attribute on body
        // It was being set on none at overlay opening and was preventing
        // automated tests to click on other elements while the overlay was open
        element: this.view.dom.$overlay,
        event: ContextMenuOverlayEvents.OPEN,
        callback: overlayOpenListener,
      },
      {
        element: this.view.dom.$overlayList,
        event: MultiLevelListEvents.ITEM_CLICKED,
        callback: this.handleActionClickFromOverlay.bind(this),
      },
      {
        element: this.view.dom.$wrapper,
        event: NETWORK_DATA_PROVIDER_ERROR,
        callback: this.handleGridRequestError.bind(this),
      },
      {
        element: this.view.dom.$navButtonTop,
        event: ButtonEvents.CLICK,
        callback: this.handleTopNavButtonClick.bind(this),
      },
      {
        element: this.view.dom.$navButtonBottom,
        event: ButtonEvents.CLICK,
        callback: this.handleBottomNavButtonClick.bind(this),
      },
      {
        element: this.view.dom.$networkScrollableWrapper,
        event: NavButtonEvent.NAV_BUTTON_DISPLAY_CHANGE,
        callback: this.handleNetworkScrollableWrapperChange.bind(this),
      },
      {
        element: this.view.dom.$drillDownStatusChip,
        event: GridViewStatusChipEvent.STATUS_CLICK,
        callback: this.handleDrillDownStatusChipClick.bind(this),
      },
    ];
  }

  private handleDrillDownStatusChipClick(event: WebComponentEvent): void {
    // TODO: Implement drill down status chip click handler
    console.log('Drill down status chip clicked', event);
  }

  private handleNetworkScrollableWrapperChange(event: CustomEvent): void {
    const { buttonType, isVisible } = event.detail;
    if (buttonType === NavButtonType.TOP) {
      this.view.dom.$navButtonTop.style.display = isVisible ? 'flex' : 'none';
    } else if (buttonType === NavButtonType.BOTTOM) {
      this.view.dom.$navButtonBottom.style.display = isVisible ? 'flex' : 'none';
    }
  }

  private handleTopNavButtonClick(): void {
    this.view.dom.$networkScrollableWrapper.handleScrollToTarget(TargetType.TOP);
  }

  private handleBottomNavButtonClick(): void {
    this.view.dom.$networkScrollableWrapper.handleScrollToTarget(TargetType.BOTTOM);
  }

  private handleRefreshGrid() {
    this.view.refreshGrid();
  }

  async getDataForView(): Promise<InitNetworkData> {
    const externalParams = this.component.externalParams;

    // Add MDR Company
    if (Application.Main.getUser().mdrSuperAnalyst) {
      const company = Application.Main.getActiveCompany();

      // This is how we add an external company filter, this is similar to how we'll add drill-down filters
      //const companyFilter = externalParams.filters?.find(filter => filter.company);
      //if (companyFilter) {
      //  companyFilter.company.equals = company.id;
      //} else {
      //  externalParams.filters = externalParams.filters || [];
      //  externalParams.filters.push({ company: { equals: company.id } });
      //}

      externalParams.treeDetails = await getTreeDetailsForCompanyId(company.id);
    }

    return {
      config: await this.dataRepository.computeUserConfig(),
      externalParams: this.component.externalParams,
    };
  }

  private onChangeDepth(event: WebComponentEvent) {
    this.view.isRecursiveDepth = event.detail.checked;
    this.view.refreshGrid();

    this.syncStateInNetworkStorage();
  }

  private syncStateInNetworkStorage() {
    const currentSmartViewState: SmartViewState = Ext.state.Manager.get(this.view.smartViewsComponentName, {});
    const activeViewConfiguration = SmartViewsHelper.processSmartViewForUpdate(this.view.dom.$grid.activeViewProps);
    const activeViewInitialConfiguration = SmartViewsHelper.processSmartViewForUpdate(
      this.view.dom.$grid.activeViewInitialProps
    );

    if (isEqual(activeViewConfiguration, activeViewInitialConfiguration)) {
      // we can discard everything since this change actually returns our view to the initial state
      Ext.state.Manager.set(this.view.smartViewsComponentName, { selectedViewId: activeViewConfiguration.id });
      return;
    }

    // we persist our changing
    currentSmartViewState.savedViewConfiguration = {
      ...activeViewConfiguration.details.specifics,
      selectedViewId: activeViewConfiguration.id,
    };

    return Ext.state.Manager.set(this.view.smartViewsComponentName, currentSmartViewState);
  }

  private handleTreeChange(event: CustomEvent) {
    // If we're on the root node of a read-only view, and we're clicking on the same root node, we don't want to show any changes
    if (
      this.view.dom.$grid.activeViewProps.treeDetails === undefined &&
      this.view.dom.$tree.controller.isRootNode(event.detail.nodeInfo.targetId)
    ) {
      return;
    }

    this.view.treeNodeInfo = event.detail.nodeInfo;
    this.view.refreshGrid();

    this.syncStateInNetworkStorage();
  }

  private handleTreeStartFetching() {
    toggleVisibility(this.view.dom.$smartViews, false);
  }

  private handleTreeInputReset() {
    toggleVisibility(this.view.dom.$smartViews, true);
  }

  private handleNameClicked(event: CustomEvent) {
    const entityData = event.detail as ProtectedEntity;
    if (!entityData._id) {
      console.error('Could not find any protected entity to open endpoint details for.');
      return;
    }

    if (ProtectedEntitiesHelper.isEndpoint(entityData)) {
      NavigationHelper.navigateToNetworkAndShowDetailsPanel(entityData._id);
      return;
    }

    if (this.nameLinkDisabled && this.view.dom.$depthToggle.checked) {
      console.error('Feature got disabled due to performance issues.');
      return;
    }

    const nodeInfo: TreeDetails = {
      targetId: entityData._id,
      path: entityData.path,
      parentId: entityData.parentId,
      type: entityData.type,
    };

    this.view.treeNodeInfo = nodeInfo;
    this.view.dom.$tree.propertiesSet = false;
    this.view.buildTreeView(nodeInfo);
    this.view.refreshGrid();
  }

  private handleUserColumnClicked(event: CustomEvent) {
    const entityData = event.detail as ProtectedEntity;

    if (!entityData._id) {
      console.error('Could not find any protected entity to open endpoint details for.');
      return;
    }

    const detailsController = Application.modules.details.controller.details;
    detailsController.setNextSelectedTab('network-details-computerTabEndpointUsers');

    NavigationHelper.navigateToNetworkAndShowDetailsPanel(entityData._id);
  }

  private handleGridSelectRows(event: WebComponentEvent): void {
    // this array contains all the data from the selected items in the current page of the grid
    const currentPageItemsData = event.detail.selectedItemsData as Array<NetworkGridItem>;
    // this array contains only the ids of the all selected items globally aka from all pages
    const allSelectedItemsIds = event.detail.selectionStoreData as Array<string>;

    // add not found items
    for (const id of allSelectedItemsIds) {
      if (!this.selectedItemsData.find(item => item._id === id)) {
        const currentItem = currentPageItemsData.find(item => item._id === id);
        if (!currentItem) {
          console.error('Could not find current item.');
          continue;
        }

        this.selectedItemsData.push(currentItem);
      }
    }

    // filter removed items
    this.selectedItemsData = this.selectedItemsData.filter(item => allSelectedItemsIds.includes(item._id));

    if (this.selectedItemsData.length > 0) {
      this.view.toggleGlobalActionsButtons(true);
      this.view.toggleGoToContainer(this.selectedItemsData.length === 1);
      this.view.toggleDeleteEntitiesButton(true);
      return;
    }

    this.view.toggleGlobalActionsButtons(false);
    this.view.toggleGoToContainer(false);
    this.view.toggleDeleteEntitiesButton(false);
  }

  private computeMegaMenuSections(event: WebComponentEvent): void {
    this.megaMenuSections = MegaMenuHelper.getMegaMenuSections();

    this.view.setMegaMenuSections(this.megaMenuSections);

    if (!this.megaMenuActions.length) {
      this.megaMenuActions = this.megaMenuSections
        .map((section: MegaMenuSection) => section.items)
        .flat()
        .map((item: MegaMenuItem) => item.event as MegaMenuAction);
    }
  }

  private handleGlobalActionsButtonClick(event: WebComponentEvent): void {
    this.view.dom.$megaMenu.resetInputSearch();

    this.setDisabledMenuSections(this.selectedItemsData);
    this.view.setMegaMenuSections(this.megaMenuSections);

    this.view.dom.$globalActionsOverlay.open = true;
  }

  private setDisabledMenuSections(selectedItemsData) {
    const actionsValidationHistoryMap: ValidationHistoryMap = {};

    for (const action of this.megaMenuActions) {
      const validator: BaseNetworkActionValidator = NetworkActionValidatorsFactory.create(action);

      if (actionsValidationHistoryMap[action] === undefined) {
        actionsValidationHistoryMap[action] = true;
      }

      if (validator.shouldValidateInBulk()) {
        const records = selectedItemsData.map(item => item.dbRecord);
        actionsValidationHistoryMap[action] = validator.canPerformBulkAction(records);
        continue;
      }

      for (const gridItem of selectedItemsData) {
        const record: ProtectedEntity = gridItem.dbRecord;
        /**
         *  false && function() ---> function is not called
         *  this means that if the action failed to validate in a previous record, validation will not be performed again
         */

        actionsValidationHistoryMap[action] = actionsValidationHistoryMap[action] && validator.canPerformAction(record);
      }
    }

    for (const actionState of Object.entries(actionsValidationHistoryMap)) {
      const [action, canPerformAction] = actionState;
      this.megaMenuSections = this.megaMenuSections.map(
        (section: MegaMenuSection): MegaMenuSection => ({
          ...section,
          items: section.items.map(
            (item: MegaMenuItem): MegaMenuItem =>
              item.event === action ? { ...item, disabled: !canPerformAction } : item
          ),
        })
      );
    }
  }

  private handleReportsButtonClick(event: WebComponentEvent): void {
    const extDependencies = {
      getModuleByName: app.getModuleByName.bind(app),
      getAvailableServices: Application.Main.getAvailableServices.bind(Application.Main),
      Constants: { ServiceTypes: { EPS: Constants.ServiceTypes.EPS } },
    };

    const targets = this.selectedItemsData.map(selectedItem => {
      const record = selectedItem.dbRecord;
      return {
        protectedEntityId: record._id,
        name: record.name,
        type: record.type,
        isSva: !!record.specifics.isSva,
        path: record.path,
        company: record.company,
      };
    });

    NavigationHelper.showReportFromNetwork(extDependencies, targets);
  }

  private async handleDeleteEntitiesButtonClicked(event: WebComponentEvent): Promise<void> {
    this.view.toggleSpinnerDeleteEntitiesButton(true);

    await DeleteActionHelper.displayDeletePopUp(
      this.selectedItemsData.map(item => item.dbRecord),
      this.view.dom.$wrapper
    );

    this.view.toggleSpinnerDeleteEntitiesButton(false);
  }

  private handleRecoveryManagerButtonClick() {
    NetworkPopUpFactory.getContent(NetworkPopUpType.RECOVERY_MANAGER);
  }

  private async handleMegaMenuItemClick(event: WebComponentEvent): Promise<void> {
    this.view.dom.$globalActionsOverlay.open = false;

    const action: MegaMenuAction = event.detail;

    const popUpRenderData: PopUpRenderData = await this.getPopUpRenderData(action);

    if (popUpRenderData.isError) {
      NetworkPopUpFactory.getContent(NetworkPopUpType.ACTION_ERROR_POP_UP, {
        specifics: popUpRenderData.errorData,
        targets: [],
        parentElementRef: this.view.dom.$wrapper,
      });
      return;
    }

    const config = NetworkActionsToPopupsMap.get(action);

    if (!config) {
      console.error('No popup type found for action', action);
      return;
    }

    const protectedEntities: Array<ProtectedEntity> = this.selectedItemsData.map(item => item.dbRecord);

    NetworkPopUpFactory.getContent(
      config.popupType,
      {
        ...config.extraPopupData,
        targets: protectedEntities,
        specifics: popUpRenderData,
        parentElementRef: this.view.dom.$wrapper,
        showOnlyConfirm: popUpRenderData.isError ?? false,
      },
      this.dependencies
    );

    analytics.trackPageInteraction({
      reference: `${NetworkV2Analytics.TRACKING_NAME}:${NetworkV2Analytics.IDENTIFIER_ACTION_CLICKED}`,
      asset: action,
    });
  }

  private async handleGridSelectAll() {
    // settings loading to the grid
    this.view.dom.$grid.controller.view.setElementClass(
      this.view.dom.$grid.controller.view.dom.$wrapper,
      DATA_LOAD_MORE
    );

    // get the rows
    const data = await this.view.dom.$grid.dataProvider.getRows({
      columns: this.view.dom.$grid.columns,
      filters: this.view.dom.$grid.filters,
      extraParams: { ...this.view.dom.$grid.extraParams, ignoreLimit: true },
      pageNumber: 1,
      pageSize: this.view.dom.$grid.pageSize,
    });

    // removing loading from grid
    this.view.dom.$grid.controller.view.removeElementClass(
      this.view.dom.$grid.controller.view.dom.$wrapper,
      DATA_LOAD_MORE
    );

    // setting selectedItems
    this.selectedItemsData = data.rawEvents as Array<NetworkGridItem>;
  }

  private handleSmartViewClicked(event: WebComponentEvent) {
    const smartViewConfiguration = event.detail.smartViewConfiguration;
    if (this.view.dom.$grid.viewId === event.detail.smartViewConfiguration._id) {
      if (this.view.isCurrentViewEdited()) {
        this.view.dom.$grid.controller.discardViewChanges();
      }
      return;
    }

    GuidedToursManager.exitGuidedTour(GuidedTourSections.NETWORK);

    if (!smartViewConfiguration.specifics.isDefault && smartViewConfiguration.isReadOnly) {
      const viewTrackingTag = SmartViewIdToAnalyticsNameMap.get(smartViewConfiguration._id);

      if (viewTrackingTag) {
        analytics.trackPageInteraction({
          reference: `${NetworkV2Analytics.TRACKING_NAME}:${NetworkV2Analytics.IDENTIFIER_SMART_VIEW_CLICKED}`,
          asset: `${viewTrackingTag}`,
        });
      }
    }

    this.view.dom.$smartViews.controller.updateActiveViewId(event.detail.smartViewConfiguration._id);
    const convertedConfig = SmartViewsHelper.convertGenericConfigToGridConfig(smartViewConfiguration);
    this.handleGridReload(convertedConfig);
  }

  private updateViewProps(event: WebComponentEvent) {
    this.view.dom.$smartViews.updateSmartView(
      SmartViewsHelper.processSmartViewForUpdate(event.detail.viewConfiguration)
    );
    this.view.dom.$grid.activeViewProps = cloneDeep(event.detail.viewConfiguration);
    this.view.dom.$grid.activeViewInitialProps = cloneDeep(event.detail.viewConfiguration);
    this.view.dom.$grid.defaultViewProps = cloneDeep(event.detail.viewConfiguration);

    this.syncStateInNetworkStorage();
  }

  async showSaveAsDialog() {
    const viewConfiguration = SmartViewsHelper.processSmartViewForUpdate(this.view.dom.$grid.activeViewProps);
    const viewCreationConfig = {
      dialogText: this.view.smartViewsStrings.saveGridViewFormDialog.dialogText,
    };
    await this.view.dom.$smartViews.saveSmartView(
      SmartViewsHelper.convertGridConfigToGenericConfig(viewConfiguration),
      viewCreationConfig
    );
  }

  private handleSmartViewAfterCreate(event: WebComponentEvent) {
    this.view.buildGrid(SmartViewsHelper.convertGenericConfigToGridConfig(event.detail.createdView));
  }

  private handleSmartViewNameUpdated(event: WebComponentEvent) {
    GenericGridSmartViewsHelpers.handleSmartViewNameUpdated(event, {
      gridElement: this.view.dom.$grid,
      smartViewsElement: this.view.dom.$smartViews,
    });
  }

  private handleSmartViewAfterDelete(event: WebComponentEvent) {
    const view = event.detail.deletedView;
    if (view._id === this.view.dom.$grid.viewId) {
      const activeView = this.view.dom.$smartViews.getActiveView();
      const gridActiveView = SmartViewsHelper.convertGenericConfigToGridConfig(activeView);
      this.view.buildGrid(gridActiveView);
      this.view.isRecursiveDepth = !!gridActiveView.recursive;
    }
  }

  handleSmartViewFavoriteChanged(event: WebComponentEvent) {
    GenericGridSmartViewsHelpers.handleSmartViewFavoriteChanged({
      gridElement: this.view.dom.$grid,
      smartViewsElement: this.view.dom.$smartViews,
    });
  }

  private async handleDiscardChanges() {
    const smartViewConfiguration = this.view.dom.$grid.activeViewProps;
    this.handleGridReload(smartViewConfiguration);
  }

  private handleGridReload(view: NetworkViewGridConfig) {
    this.view.isRecursiveDepth = !!view.recursive;
    const treeDetails = view.treeDetails
      ? view.treeDetails
      : this.view.dom.$tree.controller.getActiveTargetInfoFromNode(this.view.dom.$tree.rootNode);
    this.view.buildTreeView(treeDetails);
    this.view.treeNodeInfo = treeDetails;
    this.view.buildGrid(view);
  }

  private handleGoToContainer() {
    //only one element should be in grid
    if (this.selectedItemsData.length !== 1) {
      console.error('Could not handle go to container for multiple entities.');
      return;
    }

    const selectedTarget = this.selectedItemsData[0];

    this.view.dom.$tree.propertiesSet = false;
    this.view.buildTreeView(
      {
        targetId: selectedTarget._id,
        path: selectedTarget.dbRecord.path,
        parentId: selectedTarget.dbRecord.parentId,
        type: selectedTarget.type,
      },
      true
    );
  }

  private refreshGridAndTree(): void {
    this.view.refreshGrid();
    this.view.buildTreeView(undefined);
  }

  private refreshTree(): void {
    this.view.dom.$tree.propertiesSet = true;
  }

  private async getPopUpRenderData(action: NetworkAction): Promise<PopUpRenderData> {
    return await NetworkActionsHelperFactory.getHelper(action).processAction(
      this.selectedItemsData.map(item => item.dbRecord)
    );
  }

  private handleMoveFinishedAction(event: CustomEvent) {
    const data = event.detail as MoveFinishedEventDetail | undefined;
    if (!data) {
      this.refreshGridAndTree();
      return;
    }

    this.view.refreshGrid();
    const oldRecords = data.oldRecords;
    const newRecords = data.newRecords;

    if (oldRecords.length !== newRecords.length) {
      throw new Error('Invalid number of records returned from backend.');
    }

    for (let i = 0; i < data.oldRecords.length; i++) {
      const oldRecord = data.oldRecords[i];
      const newRecord = data.newRecords[i];

      this.checkIfTreeDetailsNodePathGotChanged(oldRecord.path, newRecord.path, newRecord.parentId);
    }

    this.refreshTree();
  }

  /**
   * When moving entities we need to set treeDetails path and parentId if the moving action changes it.
   * @param oldRecordPath
   * @param newRecordPath
   * @param parentId
   * @private
   */
  private checkIfTreeDetailsNodePathGotChanged(oldRecordPath: string, newRecordPath: string, parentId: string) {
    const treeDetails = this.view.dom.$tree.treeDetails as TreeDetails;
    const treeDetailsOldPaths = treeDetails.path.split('/');
    const recordOldPaths = oldRecordPath.split('/');

    const isInSubtree = treeDetailsOldPaths.slice(0, recordOldPaths.length).join('/') === oldRecordPath;
    if (!isInSubtree) {
      return;
    }

    const relativePath = treeDetailsOldPaths.slice(recordOldPaths.length).join('/');
    const newPath = newRecordPath + (relativePath ? '/' + relativePath : '');

    if (treeDetails.path !== newPath) {
      this.view.dom.$tree.treeDetails = {
        ...treeDetails,
        path: newPath,
        parentId: parentId,
      };
    }
  }

  private handleChangedAD(event: NewADFolderEvent): void {
    if (event.detail.refreshTree) {
      // refresh company from tree
      this.view.dom.$tree.propertiesSet = false;
      this.view.treeNodeInfo = event.detail.refreshTree;
      this.view.buildTreeView(event.detail.refreshTree);
    }

    // refresh network grid
    this.view.refreshGrid();
  }

  private handleDeleteEntities(event: CustomEvent) {
    const deletedRecords = event.detail?.records as Array<ProtectedEntity> | undefined;

    // I think that this should never happen on success.
    if (!deletedRecords || deletedRecords.length === 0) {
      console.error('No refresh tree or grid needed. Delete entities failed.');
      return;
    }

    const currentSelectedNodePath = this.view.dom.$tree.treeDetails.path;
    for (const record of deletedRecords) {
      if (currentSelectedNodePath.includes(record.path)) {
        this.view.dom.$tree.treeDetails = {
          targetId: record._id,
          ...record,
        };
        this.view.dom.$tree.findParentNode = true;
        this.view.dom.$tree.propertiesSet = true;
        return;
      }
    }

    this.view.dom.$tree.findParentNode = false;
    this.view.dom.$tree.propertiesSet = true;
    this.view.refreshGrid();
  }

  private handleEC2SyncInitiated() {
    // just make the loading visible
    elementClassListRemove(this.view.dom.$globalLoading, 'hidden');
  }

  private handleEC2SyncFinished(event: CustomEvent) {
    elementClassListAdd(this.view.dom.$globalLoading, 'hidden');
    const target = event.detail.target as ProtectedEntity;
    const treeDetails: TreeDetails = {
      targetId: target._id,
      ...target,
    };

    // the selection from tree needs to be set to the amazon group root
    if (!target) {
      console.error('Could not find the initial target of sync.');
      return;
    }

    const findParentNode = target.type !== Constants.ProtectedEntities.EPS.TYPE_AWS_GROUP;

    this.view.dom.$tree.propertiesSet = false;
    this.view.buildTreeView(treeDetails, findParentNode);

    if (!findParentNode) {
      this.view.treeNodeInfo = treeDetails;
      this.view.refreshGrid();
    }
  }

  private prepareFiltersTourElement() {
    const response: GuidedTourSetterResponse = this.view.setFiltersTourTargetElement();

    if (!response.alreadyIncreasedCounter) {
      this.guidedTourElementsLoaded++;
    }

    this.showGuidedTourIfPossible();
  }

  private prepareTreeTourElement() {
    let response: GuidedTourSetterResponse = this.view.setTreeTourTargetElement();

    if (!response.alreadyIncreasedCounter) {
      this.guidedTourElementsLoaded++;
    }

    // smart views tour elements are dependent on tree tour elements
    // because the tree expands the div before smart views
    response = this.view.setSmartViewsTourTargetElement();

    if (!response.alreadyIncreasedCounter) {
      this.guidedTourElementsLoaded++;
    }

    this.showGuidedTourIfPossible();
  }

  private showGuidedTourIfPossible() {
    if (this.guidedTourElementsLoaded !== NETWORK_GUIDED_TOUR_HTML_ELEMENTS_NEED_PREPARING) {
      return;
    }

    this.view.setGuidedTourTargetElementsOrder();

    this.view.showGuidedTour();
  }

  private prepareIconsAndActionsTourElements() {
    let response: GuidedTourSetterResponse = this.view.setIconsTourTargetElement();

    if (!response.alreadyIncreasedCounter) {
      this.guidedTourElementsLoaded++;
    }

    response = this.view.setMegaMenuTourTargetElement();

    if (!response.alreadyIncreasedCounter) {
      this.guidedTourElementsLoaded++;
    }

    this.showGuidedTourIfPossible();
  }

  private handleRowRightClicked(event: RowRightClickEvent) {
    const { coords, selectedItems } = event.detail;
    this.setDisabledMenuSections(selectedItems);

    const items: MultiLevelListItem[] = this.megaMenuSections.map((section: MegaMenuSection): MultiLevelListItem => {
      return {
        name: section.label,
        value: '',
        children: MegaMenuHelper.getItemsForRightClickOverlay(section.items),
      };
    });

    const quickActions = this.megaMenuSections.map(section => section.items.filter(item => item.isQuickAction)).flat();

    items.unshift({
      name: this.view.dom.$megaMenu.quickActionsString,
      value: '',
      children: MegaMenuHelper.getItemsForRightClickOverlay(quickActions),
    });

    const additionalButtons: MultiLevelListItem[] = [
      {
        name: this.view.buttonStrings.reports,
        value: RightClickExtraActions.REPORTS,
      },
      {
        name: this.view.buttonStrings.goTo,
        value: RightClickExtraActions.GO_TO,
        disabled: selectedItems.length !== 1,
      },
      {
        name: this.view.buttonStrings.delete,
        value: RightClickExtraActions.DELETE,
        disabled: selectedItems.length === 0,
        textColor: 'danger',
      },
    ].filter(item => !item.disabled);

    items.push(...additionalButtons);
    const validParentItems = items.filter(item => item.children === undefined || item.children.length > 0);

    // we add divider before the "Reports" action (which is the first additional option and unconditioned)
    const reportsActionIndex = validParentItems.findIndex(item => item.value === RightClickExtraActions.REPORTS);
    if (reportsActionIndex !== 0) {
      validParentItems[reportsActionIndex - 1].hasDivider = true;
    }

    this.view.handleRowRightClicked(coords, validParentItems);
  }

  handleActionClickFromOverlay(event: WebComponentEvent) {
    switch (event.detail.value) {
      case RightClickExtraActions.REPORTS: {
        this.handleReportsButtonClick(event);
        break;
      }
      case RightClickExtraActions.GO_TO: {
        this.handleGoToContainer();
        break;
      }
      case RightClickExtraActions.DELETE: {
        this.handleDeleteEntitiesButtonClicked(event);
        break;
      }
      default: {
        const wrappedEvent = new CustomEvent('', { detail: event.detail.value }) as WebComponentEvent;
        this.handleMegaMenuItemClick(wrappedEvent);
      }
    }

    this.view.dom.$overlay.close();
  }

  private handleGridRequestError(error) {
    if (error.detail.exceptionType.includes('TargetsException')) {
      const rootNode = this.view.dom.$tree.rootNode.originalData;
      const treeDetails: TreeDetails = {
        targetId: rootNode._id,
        path: rootNode.path,
        parentId: rootNode.parentId,
        type: rootNode.type,
      };

      this.view.buildTreeView(treeDetails, true);
      this.view.treeNodeInfo = treeDetails;
      this.view.refreshGrid();
    }
  }
}
